import React, { useState, useEffect } from 'react';
import { BarChart3, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getActivePolls, submitVote } from '../services/api';
import SEO from '../components/SEO';

const PollCard = ({ poll, handleVote, hasVoted, totalVotes }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onVoteSubmit = async () => {
    if (!selectedOption || hasVoted || isSubmitting) return;
    setIsSubmitting(true);
    await handleVote(poll.id, selectedOption);
    setIsSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-6 shadow-sm border border-stone-200 flex flex-col h-full hover:shadow-lg transition-shadow duration-300"
    >
      <h3 className="text-xl font-extrabold text-stone-800 mb-6 leading-snug">
        {poll.question}
      </h3>

      <div className="space-y-4 flex-grow">
        {poll.options.map(option => {
          const isWinner = hasVoted && option.votes === Math.max(...poll.options.map(o => o.votes));
          const optionPercentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
          const isSelected = selectedOption === option.id;

          return (
            <div key={option.id} className="relative">
              {!hasVoted ? (
                <button
                  onClick={() => setSelectedOption(option.id)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center ${
                    isSelected
                      ? 'border-saffron-500 bg-saffron-50 bg-opacity-50'
                      : 'border-stone-100 hover:border-saffron-300 hover:bg-stone-50'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${isSelected ? 'border-saffron-500' : 'border-stone-300'}`}>
                    {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-saffron-500"></div>}
                  </div>
                  <span className={`font-medium ${isSelected ? 'text-saffron-700' : 'text-stone-700'}`}>
                    {option.option_text}
                  </span>
                </button>
              ) : (
                <div className="relative overflow-hidden rounded-xl bg-stone-50 border border-stone-100 h-14 flex items-center px-4">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${optionPercentage}%` }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    className={`absolute left-0 top-0 bottom-0 ${isWinner ? 'bg-saffron-500' : 'bg-stone-200'} opacity-20`}
                  ></motion.div>
                  <div className="relative z-10 flex w-full justify-between items-center font-medium">
                    <span className={isWinner ? 'text-saffron-700 font-bold' : 'text-stone-700'}>
                      {option.option_text}
                    </span>
                    <span className="text-stone-600 font-bold ml-2">
                      {optionPercentage}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex justify-between items-center pt-4 border-t border-stone-100">
        <div className="text-sm text-stone-500 font-medium">
          {totalVotes} {totalVotes === 1 ? 'Vote' : 'Votes'} Total
        </div>
        
        {!hasVoted ? (
          <button
            onClick={onVoteSubmit}
            disabled={!selectedOption || isSubmitting}
            className={`px-6 py-2.5 rounded-lg font-bold transition-all duration-200 ${
              selectedOption && !isSubmitting
                ? 'bg-saffron-600 text-white hover:bg-saffron-700 shadow-md'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Vote'}
          </button>
        ) : (
          <div className="flex items-center text-green-600 font-bold text-sm bg-green-50 px-3 py-1.5 rounded-lg">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Voted
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Polls = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votedPollIds, setVotedPollIds] = useState([]);

  useEffect(() => {
    // Check local storage for list of polls this browser has already voted on
    const storedVotes = JSON.parse(localStorage.getItem('nathnagri_voted_polls') || '[]');
    setVotedPollIds(storedVotes);

    const loadPolls = async () => {
      const data = await getActivePolls();
      setPolls(data);
      setLoading(false);
    };
    loadPolls();
  }, []);

  const handleVote = async (pollId, optionId) => {
    // Optimistic Update
    const updatedPolls = polls.map(p => {
      if (p.id === pollId) {
        const newOptions = p.options.map(opt => {
          if (opt.id === optionId) {
            return { ...opt, votes: opt.votes + 1 };
          }
          return opt;
        });
        return { ...p, options: newOptions };
      }
      return p;
    });

    setPolls(updatedPolls);
    
    const newVotedList = [...votedPollIds, pollId];
    setVotedPollIds(newVotedList);
    localStorage.setItem('nathnagri_voted_polls', JSON.stringify(newVotedList));

    // Send to backend
    await submitVote(optionId);
  };

  return (
    <div className="bg-stone-50 min-h-screen pt-12 pb-24 border-t border-stone-200">
      <SEO 
        title="Community Polls | Nathnagri" 
        description="Participate in community polls about Bareilly's temples, faith, and local spiritual topics." 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <div className="bg-saffron-100 p-4 rounded-full">
              <BarChart3 className="h-8 w-8 text-saffron-600" />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
            समुदाय <span className="text-saffron-600">सर्वेक्षण (Polls)</span>
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            नाथ नगरी के विकास और हमारी आध्यात्मिक यात्रा के बारे में अपनी राय साझा करें। प्रत्येक विषय पर अपना पक्ष चुनें और देखें कि अन्य भक्त क्या सोचते हैं।
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600"></div>
          </div>
        ) : polls.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center border border-stone-200 shadow-sm max-w-3xl mx-auto">
            <BarChart3 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-stone-800 mb-2">No Active Polls</h2>
            <p className="text-stone-500">Check back later for new community polls and questions.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <AnimatePresence>
              {polls.map((poll) => {
                const totalVotes = poll.options.reduce((acc, curr) => acc + curr.votes, 0);
                const hasVoted = votedPollIds.includes(poll.id);
                
                return (
                  <PollCard 
                    key={poll.id} 
                    poll={poll} 
                    totalVotes={totalVotes}
                    hasVoted={hasVoted}
                    handleVote={handleVote}
                  />
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default Polls;
